/**
 * Blur / pixelate region rendering.
 *
 * One implementation shared by the live preview (canvasRenderer) and the
 * in-browser exporter (videoExporter), so what you see on the canvas is what
 * lands in the file.
 *
 * Why this module exists
 * ----------------------
 * The two call sites each had their own copy of the maths, and both were
 * wrong in different ways:
 *
 *  - The exporter sampled the *video element* using overlay bounds that
 *    ExportPanel had already scaled to the output resolution. Exporting a
 *    720p source at the default 1080p made every blur region sample a rect
 *    1.5x too large and offset; regions near the right or bottom edge fell
 *    outside the source frame entirely, so `drawImage` copied nothing and the
 *    "blurred" patch was fully transparent. That is why turning intensity up
 *    to 100 changed nothing — there was no pixel data to blur.
 *
 *  - The preview padded the sampled rect by `blurAmount * 2` in *video*
 *    pixels but drew it into a destination expanded by the same number of
 *    *canvas* pixels, so whenever the preview wasn't at 1:1 scale the blurred
 *    patch was stretched and shifted relative to the region it was meant to
 *    cover.
 *
 * Both are avoided here by taking the source rect and the destination rect as
 * explicit, separate inputs and deriving every other number from them.
 *
 * Intensity mapping
 * -----------------
 * The old code used a fixed pixel radius (`intensity / 5`), which meant the
 * same setting produced a wildly different look at 720p and at 4K, and even
 * at 100 was too weak to actually obscure a large region. Radius is now a
 * fraction of the region's shorter side, so intensity means the same thing at
 * every resolution: 100 blurs by a quarter of the region, which is thoroughly
 * unreadable, and pixelate at 100 leaves roughly eight blocks across.
 */

export interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** Clamp `value` into [min, max]. */
const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value));

/**
 * Gaussian blur radius, in destination pixels.
 *
 * CSS `blur(<length>)` uses the length as the Gaussian standard deviation, so
 * this value maps 1:1 onto FFmpeg's `gblur=sigma=` on the backend.
 */
export const getBlurRadius = (intensity: number, dstWidth: number, dstHeight: number): number => {
  const shorterSide = Math.max(1, Math.min(dstWidth, dstHeight));
  return Math.max(2, (shorterSide * clamp(intensity, 0, 100)) / 400);
};

/** Pixelate block size, in destination pixels. */
export const getPixelateSize = (intensity: number, dstWidth: number, dstHeight: number): number => {
  const shorterSide = Math.max(1, Math.min(dstWidth, dstHeight));
  return Math.max(2, Math.round((shorterSide * clamp(intensity, 0, 100)) / 800));
};

/**
 * Draw a blurred or pixelated copy of a region of `source` onto `ctx`.
 *
 * @param srcRect    region to sample, in the SOURCE's own pixel space
 * @param dstRect    where to paint it, in the TARGET canvas's space
 * @param sourceSize intrinsic size of `source`, used to clamp the sample rect
 *
 * @returns false when there was nothing to sample (region fully off-source),
 *          so the caller can fall back to a placeholder instead of silently
 *          painting nothing — the old failure mode.
 */
export const drawBlurredRegion = (
  ctx: CanvasRenderingContext2D,
  source: CanvasImageSource,
  srcRect: Rect,
  dstRect: Rect,
  options: { blurType: 'blur' | 'pixelate'; intensity: number },
  sourceSize: { width: number; height: number }
): boolean => {
  const dstW = Math.max(1, Math.round(dstRect.width));
  const dstH = Math.max(1, Math.round(dstRect.height));

  if (srcRect.width <= 0 || srcRect.height <= 0) return false;
  if (sourceSize.width <= 0 || sourceSize.height <= 0) return false;

  const isPixelate = options.blurType === 'pixelate';
  const radius = getBlurRadius(options.intensity, dstW, dstH);

  // Sample a margin beyond the region so the Gaussian has real pixels to pull
  // from at the edges; without it the blur fades into transparency and leaves
  // a bright halo around the region. Pixelate needs no margin.
  const pad = isPixelate ? 0 : Math.ceil(radius * 2);

  const offW = dstW + pad * 2;
  const offH = dstH + pad * 2;

  // The margin is expressed in destination pixels, so convert it into source
  // pixels using this region's own scale factor. (Mixing the two spaces is
  // exactly what made the preview's blurred patch drift out of position.)
  const srcPerDstX = srcRect.width / dstW;
  const srcPerDstY = srcRect.height / dstH;
  const padSrcX = pad * srcPerDstX;
  const padSrcY = pad * srcPerDstY;

  // Expanded sample rect, then clamped to the source. Clamping matters: a
  // region touching the frame edge would otherwise ask drawImage for pixels
  // that don't exist.
  const expX0 = srcRect.x - padSrcX;
  const expY0 = srcRect.y - padSrcY;
  const expX1 = srcRect.x + srcRect.width + padSrcX;
  const expY1 = srcRect.y + srcRect.height + padSrcY;

  const clipX0 = clamp(expX0, 0, sourceSize.width);
  const clipY0 = clamp(expY0, 0, sourceSize.height);
  const clipX1 = clamp(expX1, 0, sourceSize.width);
  const clipY1 = clamp(expY1, 0, sourceSize.height);

  const clipW = clipX1 - clipX0;
  const clipH = clipY1 - clipY0;
  if (clipW <= 0 || clipH <= 0) return false;

  const offscreen = document.createElement('canvas');
  offscreen.width = offW;
  offscreen.height = offH;
  const offCtx = offscreen.getContext('2d');
  if (!offCtx) return false;

  // Where the clamped sample rect lands inside the offscreen canvas, which
  // spans the full (unclamped) expanded rect.
  const scaleX = offW / (expX1 - expX0);
  const scaleY = offH / (expY1 - expY0);
  const placeX = (clipX0 - expX0) * scaleX;
  const placeY = (clipY0 - expY0) * scaleY;
  const placeW = clipW * scaleX;
  const placeH = clipH * scaleY;

  try {
    if (isPixelate) {
      const pixelSize = getPixelateSize(options.intensity, dstW, dstH);
      const smallW = Math.max(1, Math.round(offW / pixelSize));
      const smallH = Math.max(1, Math.round(offH / pixelSize));

      // Down-sample into a scratch canvas, then blow it back up with
      // smoothing off. Using a second canvas rather than reading back from
      // the one being written keeps this well-defined across engines.
      const scratch = document.createElement('canvas');
      scratch.width = smallW;
      scratch.height = smallH;
      const scratchCtx = scratch.getContext('2d');
      if (!scratchCtx) return false;

      scratchCtx.drawImage(
        source,
        clipX0, clipY0, clipW, clipH,
        (placeX / offW) * smallW,
        (placeY / offH) * smallH,
        (placeW / offW) * smallW,
        (placeH / offH) * smallH
      );

      offCtx.imageSmoothingEnabled = false;
      offCtx.drawImage(scratch, 0, 0, smallW, smallH, 0, 0, offW, offH);
    } else {
      offCtx.filter = `blur(${radius}px)`;
      offCtx.drawImage(source, clipX0, clipY0, clipW, clipH, placeX, placeY, placeW, placeH);
      offCtx.filter = 'none';
    }

    // Paint back only the inner rect, discarding the sampling margin.
    ctx.drawImage(offscreen, pad, pad, dstW, dstH, dstRect.x, dstRect.y, dstW, dstH);
    return true;
  } catch {
    // Tainted canvas (cross-origin video) or an engine without ctx.filter.
    return false;
  }
};
